import dayjs from 'dayjs';

import { DOCUMENTS, noticeError } from '@ratehub/base-ui';
import { InsAutoDocument } from '@ratehub/documents/ins-auto-document/stable';
import { InsCondoDocument } from '@ratehub/documents/ins-condo-document/stable';
import { InsHomeDocument } from '@ratehub/documents/ins-home-document/stable';
import { InsLeadFormDocument } from '@ratehub/documents/ins-lead-form-document/stable';
import { InsLifeDocument } from '@ratehub/documents/ins-life-document/stable';
import { InsMotoDocument } from '@ratehub/documents/ins-moto-document/stable';

import { INS_DOCUMENT_EXPIRY_DATE_LIMIT } from '../definitions/CurrentDocument';


/**
 * Finds a document by its type and ID, or creates a new document
 * @param {Object} documentInfo
 * @param {String} documentInfo.documentId The document ID. Can be null
 * @param {String} documentInfo.documentType The document type
 * @param {Object} documentInfo.defaultSnapshot Required for sessionStore to populate user data
 * @param {Object} sessionStore 
 * @returns {Promise | Object} The found or created document
 */
function findOrCreateDocument(documentInfo, sessionStore) {
    const { documentType } = documentInfo;

    const documentModel = SUPPORTED_DOCUMENT_TYPES[documentType];

    // This should only happen if a dev supplies an invalid document type in the params
    if (!documentModel) {
        const error = new RangeError('[findOrCreateDocument] document type not supported');

        noticeError(
            error,
            {
                documentType,
                supportedDocumentTypes: Object.keys(SUPPORTED_DOCUMENT_TYPES),
            },
        );

        throw error;
    }

    return documentInfo.documentId
        ? findDocument(documentModel, documentInfo, sessionStore)
        : createNewDocument(documentModel, documentInfo, sessionStore);
}

async function findDocument(documentModel, documentInfo, sessionStore) {
    let fetchedDocument;

    try {
        fetchedDocument = await sessionStore.fetchDocumentById(
            documentInfo.documentId,
            snapshot => documentModel.create(snapshot),
        );
    } catch {
        // We don't want to log an error if we can't find the document
        // sessionStore will handle showing an error banner
        return createNewDocument(documentModel, documentInfo, sessionStore);
    }

    // ! Required
    // If we can't find the document by an ID or the document is expired, create a new document instead.
    // Documents older than 30 days are considered expired.
    // This is to handle cases where a user may bookmark a resume link.
    const documentAgeInDays = dayjs().diff(fetchedDocument?.userUpdatedAt || fetchedDocument?.updatedAt, 'day');

    if (!fetchedDocument || documentAgeInDays > INS_DOCUMENT_EXPIRY_DATE_LIMIT) {
        fetchedDocument = createNewDocument(documentModel, documentInfo, sessionStore);
    }

    return fetchedDocument;
}

function createNewDocument(documentModel, documentInfo, sessionStore) {
    const {
        documentType,
        defaultSnapshot,
    } = documentInfo;

    const snapshot = sessionStore.addProfileDataToDocumentSnapshot(
        documentType,
        defaultSnapshot,
    );

    return documentModel.create(snapshot);
}

const SUPPORTED_DOCUMENT_TYPES = {
    [DOCUMENTS.INSURANCE_AUTO.TYPE]: InsAutoDocument,
    [DOCUMENTS.INSURANCE_CONDO.TYPE]: InsCondoDocument,
    [DOCUMENTS.INSURANCE_HOME.TYPE]: InsHomeDocument,
    [DOCUMENTS.INSURANCE_LEAD_FORM.TYPE]: InsLeadFormDocument,
    [DOCUMENTS.INSURANCE_LIFE.TYPE]: InsLifeDocument,
    [DOCUMENTS.INSURANCE_MOTO.TYPE]: InsMotoDocument,
};

export default findOrCreateDocument;
